import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { isActiveOptionsList } from './constants';
import { Option, ProductCodes } from './types';
import { axios } from '../api';
import { ActiveDatesSection } from '../component/Promotions/ActiveDatesSection';
import { AutoRenewSection } from '../component/Promotions/AutoRenewSection';
import { ConditionalEligibilitySection } from '../component/Promotions/ConditionalEligibilitySection';
import { ConfirmCreatePromotionModal } from '../component/Promotions/ConfirmCreatePromotionModal';
import { FreeProductSection } from '../component/Promotions/FreeProductSection';
import { FunnelBannerSection } from '../component/Promotions/FunnelBannerSection';
import { MarketingBannerSection } from '../component/Promotions/MarketingBannerSection';
import { PromotionBasicsCard } from '../component/Promotions/PromotionBasicsCard';
import { PromotionPageShell } from '../component/Promotions/PromotionPageShell';
import { TextInput } from '../component/TextInput';
import { Button } from '../component/buttons/Button';
import { formatingOptions } from '../formatingOptions';
import { useProductsByCountry } from '../hooks/useProductsByCountry';
import { usePromotionCountryCurrency } from '../hooks/usePromotionCountryCurrency';
import { BOTTOM_TOAST, generateErrorMsg } from '../utils';
import {
  buildFunnelPromotionRequest,
  getStatusFromIsActiveOption,
  isValidPromotionName,
} from '../utils/promotionBuilders';

export const CreateFunnelPromotion = () => {
  const navigate = useNavigate();
  const isActiveOptions = useMemo(() => isActiveOptionsList, []);

  // basics
  const [promotionName, setPromotionName] = useState('');
  const [isActive, setIsActive] = useState(isActiveOptions[0]);

  // banners
  const [bannerCopy, setBannerCopy] = useState('');
  const [marketingSiteDesktopBannerCopy, setMarketingSiteDesktopBannerCopy] = useState('');
  const [marketingSiteMobileBannerCopy, setMarketingSiteMobileBannerCopy] = useState('');

  // dates
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [endDateError, setEndDateError] = useState('');

  // products
  const [selectedPromoProductOption, setSelectedPromoProductOption] = useState<Option | null>(null);
  const [selectedPaidProductOption, setSelectedPaidProductOption] = useState<Option | null>(null);
  const [funnelCheckoutDisplayName, setFunnelCheckoutDisplayName] = useState('');
  const [quantity, setQuantity] = useState('0');
  const [paidQuantity, setPaidQuantity] = useState('0');

  // auto renew + disclaimer
  const [autoRenewIntoPaidSubscription, setAutoRenewIntoPaidSubscription] = useState(false);
  const [disclaimer, setDisclaimer] = useState('');

  // conditions
  const [selectedConditionalProductIds, setSelectedConditionalProductIds] = useState<Option[]>([]);
  const [minimumSpendAmount, setMinimumSpendAmount] = useState('0');

  // modal
  const [showConfirm, setShowConfirm] = useState(false);

  // shared country/currency sync
  const { eligibleCountry, currency, handleCountryChange, handleCurrencyChange } =
    usePromotionCountryCurrency();

  // fetch products (both free + paid for funnel)
  const { freeProducts, products } = useProductsByCountry(eligibleCountry?.value, {
    includePaid: true,
  });

  const freeProductsOptions = formatingOptions(freeProducts, { value: 'id', label: 'code' });
  const productOptions = formatingOptions(products, { value: 'id', label: 'code' });

  const status = getStatusFromIsActiveOption(isActive);

  const isDisabled = () => {
    return (
      promotionName === '' ||
      !isValidPromotionName(promotionName) ||
      Number(quantity) < 0 ||
      !eligibleCountry ||
      !currency ||
      (selectedPromoProductOption !== null && Number(quantity) === 0) ||
      (selectedPromoProductOption === null && Number(quantity) > 0) ||
      !!endDateError
    );
  };

  const handleCreatePromotion = () => {
    const requestBody = buildFunnelPromotionRequest({
      promotionName,
      bannerCopy,
      marketingSiteDesktopBannerCopy,
      marketingSiteMobileBannerCopy,
      status,
      startDate,
      endDate,

      selectedPromoProductOption,
      selectedPaidProductOption,
      funnelCheckoutDisplayName,

      quantity,
      paidQuantity,

      autoRenewIntoPaidSubscription,
      disclaimer,

      selectedConditionalProductIds,
      products: products as ProductCodes[],

      minimumSpendAmount,

      currency,
      eligibleCountry,
    });

    axios
      .post('promotions', requestBody)
      .then(() => {
        navigate('/promotions');
        toast.success(`${promotionName} Promotion has been created`, BOTTOM_TOAST);
      })
      .catch((e) => {
        setShowConfirm(false);
        toast.error(generateErrorMsg(e), BOTTOM_TOAST);
      });
  };

  return (
    <PromotionPageShell
      title="Create Funnel Promotion"
      description="This promotion will apply to trial orders only. When this promotion is active, the free product(s) will be shown to customers on the Funnel checkout page, and a banner will alert them to the presence of the active promotion."
    >
      <PromotionBasicsCard
        promotionName={promotionName}
        setPromotionName={setPromotionName}
        isActive={isActive}
        isActiveOptions={isActiveOptions}
        setIsActive={setIsActive}
        currency={currency}
        eligibleCountry={eligibleCountry}
        onCurrencyChange={handleCurrencyChange}
        onCountryChange={handleCountryChange}
      />

      <FunnelBannerSection bannerCopy={bannerCopy} setBannerCopy={setBannerCopy} />

      <MarketingBannerSection
        desktopCopy={marketingSiteDesktopBannerCopy}
        setDesktopCopy={setMarketingSiteDesktopBannerCopy}
        mobileCopy={marketingSiteMobileBannerCopy}
        setMobileCopy={setMarketingSiteMobileBannerCopy}
      />

      <ActiveDatesSection
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        endDateError={endDateError}
        setEndDateError={setEndDateError}
      />

      <FreeProductSection
        eligibleCountrySelected={!!eligibleCountry}
        options={freeProductsOptions}
        selectedOption={selectedPromoProductOption}
        onChangeOption={setSelectedPromoProductOption}
        quantity={quantity}
        setQuantity={setQuantity}
      />

      <div className="bg-white mb-4 rounded-lg shadow-md p-4 min-w-[550px] relative border">
        <h2>
          Funnel checkout display name <span className="text-xs">(Optional)</span>
        </h2>
        <p className="text-xs text-gray-500 font-normal mb-2">
          Customer-facing product name to be displayed in the order summary component on funnel
          checkout
        </p>
        <div className="w-full mb-2">
          <TextInput
            placeholder="e.g., Free PupGum"
            value={funnelCheckoutDisplayName}
            setTextChange={setFunnelCheckoutDisplayName}
          />
        </div>
      </div>

      <AutoRenewSection
        autoRenewIntoPaidSubscription={autoRenewIntoPaidSubscription}
        setAutoRenewIntoPaidSubscription={setAutoRenewIntoPaidSubscription}
        eligibleCountrySelected={!!eligibleCountry}
        paidProductOptions={productOptions}
        selectedPaidProductOption={selectedPaidProductOption}
        setSelectedPaidProductOption={setSelectedPaidProductOption}
        paidQuantity={paidQuantity}
        setPaidQuantity={setPaidQuantity}
        disclaimer={disclaimer}
        setDisclaimer={setDisclaimer}
      />

      <ConditionalEligibilitySection
        eligibleCountry={eligibleCountry}
        eligibleCountrySelected={!!eligibleCountry}
        productOptions={productOptions}
        selectedConditionalProductIds={selectedConditionalProductIds}
        setSelectedConditionalProductIds={setSelectedConditionalProductIds}
        minimumSpendAmount={minimumSpendAmount}
        setMinimumSpendAmount={setMinimumSpendAmount}
      />

      <div className="flex justify-center flex-col items-center mt-2">
        <Button
          variant="primary"
          label="Create Promotion"
          onClick={() => setShowConfirm(true)}
          isDisabled={isDisabled()}
        />
      </div>

      <ConfirmCreatePromotionModal
        open={showConfirm}
        onConfirm={handleCreatePromotion}
        onCancel={() => setShowConfirm(false)}
      />
    </PromotionPageShell>
  );
};
